// Playlists as a first-class destination (Aug 2026).
//
// They existed before this page, but only INSIDE the Tunes browser — a panel
// you open from the workbench. A set list is a thing a player arrives with,
// not a thing they find while looking for something else, so it gets a route
// and a shelf on the home page.
//
// Ordering is the point of the page. A set without ordering is a bag of
// tunes, so reorder is available by drag AND by keyboard (Alt+↑/↓) — a
// drag-only list is unusable to anyone not using a mouse, and fiddly for
// everyone on a phone.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  Copy,
  GripVertical,
  Library,
  Pencil,
  Play,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';
import { playlistRows, type TuneSourceEntry } from '@/data/tuneSources';
import { openbookLibrary, type OpenbookLibraryEntry } from '@/data/openbookLibrary';
import { realbookLibrary, type RealbookLibraryEntry } from '@/data/realbookLibrary';
import { useLibraryTunes } from '@/data/libraryTunes';
import { describeSkipped, loadFirstPlayable } from '@/data/setPlayback';
import { SharePlaylist, SharedWithClass } from '@/components/SharePlaylist';
import { useOwnShares } from '@/components/useOwnShares';
import { useAuth } from '@/auth/session';
import type { PlaylistDoc, PlaylistEntry } from '@/storage/playlists';
import { canEditPlaylist, isSharedLink } from '@/storage/playlistLinks';
import { usePlaylistsAvailable } from '@/data/playlistAccess';
import { AccountSection } from '@/components/AccountSection';
import { playlistCallSummary } from '@/components/playlistCall';
import { PlaylistScoreUpdate } from '@/components/PlaylistScoreUpdate';

/**
 * Start a set: load a tune from it, arm auto-advance, and ask for playback.
 *
 * The three together are what "play this set" means. Before this you had to
 * open a tune, find the Auto toggle on the strip and press Play — three steps,
 * one of them invisible. Auto comes on HERE rather than being a switch to
 * discover: pressing Play on a set states an intent to hear the SET, not this
 * one tune.
 *
 * Playback is asked for through the store rather than called, because
 * `playAll` belongs to the workbench, which does not exist yet at the moment
 * this page asks.
 *
 * Starts at `from` but does not INSIST on it: an entry that will not load is
 * skipped and the set starts at the next one that will. A playlist entry is a
 * reference, and a set saved months ago can easily point at a tune since
 * renamed or deleted — refusing to start the whole set over one stale row
 * would be the wrong answer to "play this". The skip goes into the store so
 * the strip can explain it once the workbench is up.
 */
function useStartSet() {
  const navigate = useNavigate();
  const setAutoAdvance = useStore((s) => s.setAutoAdvanceSet);
  const requestPlayback = useStore((s) => s.requestPlayback);
  const noteSetSkip = useStore((s) => s.noteSetSkip);

  return useCallback(
    async (list: PlaylistDoc, from: PlaylistEntry, rows: readonly TuneSourceEntry[]) => {
      const { played, skipped, error } = await loadFirstPlayable(list, from, rows);
      if (!played) throw error ?? new Error('Nothing in this playlist would load.');
      noteSetSkip(describeSkipped(skipped));
      setAutoAdvance(true);
      requestPlayback();
      navigate(APP_ROUTES.workbench);
    },
    [navigate, setAutoAdvance, requestPlayback, noteSetSkip],
  );
}

/** Take an empty playlist straight to the tunes that can fill it. */
function BrowseTunesButton() {
  return (
    <Link
      className="btn btn-sm"
      data-testid="playlists-browse-tunes"
      to={{ pathname: APP_ROUTES.home, search: '?browse=all' }}
    >
      <Library size={15} aria-hidden="true" /> Browse tunes
    </Link>
  );
}

export function PlaylistsPage() {
  const { playlistId } = useParams();
  const auth = useAuth();
  const available = usePlaylistsAvailable();
  // Playlists live in your account: signed out, the page offers sign-in.
  if (!available) {
    return (
      <div className="playlists-page" data-testid="playlists-page">
        <header className="playlists-head">
          <h1>Playlists</h1>
        </header>
        {auth.status !== 'loading' && (
          <div className="shared-setlist-signin" data-testid="playlists-signin">
            <p className="muted">Sign in to see your playlists.</p>
            <AccountSection />
          </div>
        )}
      </div>
    );
  }
  // Keyed by the list: moving from one list to another (the drawer's links,
  // Duplicate) must not carry an armed Delete, a half-typed note or the share
  // link over to a list they were never meant for.
  return playlistId ? <PlaylistDetail key={playlistId} id={playlistId} /> : <PlaylistIndex />;
}

// --- the shelf --------------------------------------------------------------

function PlaylistIndex() {
  const userPlaylists = useStore((s) => s.userPlaylists);
  const savedCharts = useStore((s) => s.savedCharts);
  const startSet = useStartSet();
  // Any activated member publishes (step E, migration 0010), so only they
  // fetch or see the shared view; signed out, the index is exactly as before.
  const auth = useAuth();
  const canShare = auth.status === 'active';
  const ownShares = useOwnShares(canShare);
  const playlistLinks = useStore((s) => s.playlistLinks);
  // Only what is shared: every playlist of yours is in your account, and a
  // private one needs no mark.
  const sharing = useMemo(
    () => ownShares.shares.filter((share) => share.access !== 'none'),
    [ownShares.shares],
  );
  // Yours with sharing on, or opened from someone's link.
  const sharedIds = useMemo(
    () =>
      new Set(
        Object.entries(playlistLinks)
          .filter(([, link]) => isSharedLink(link))
          .map(([id]) => id),
      ),
    [playlistLinks],
  );
  const [openbook, setOpenbook] = useState<OpenbookLibraryEntry[]>([]);
  const [realbook, setRealbook] = useState<RealbookLibraryEntry[]>([]);
  const library = useLibraryTunes(
    userPlaylists.some((list) => list.entries.some((e) => e.ref.kind === 'library')),
  );
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    void openbookLibrary().then(setOpenbook);
    void realbookLibrary().then(setRealbook);
  }, []);

  /** Play a set from its first tune — or the first that loads. */
  const play = async (list: (typeof userPlaylists)[number]) => {
    const first = list.entries[0];
    if (!first) return;
    const rows = playlistRows(list, {
      presets: [],
      savedCharts,
      openbook,
      realbook,
      library,
      userPlaylists: [],
      playlist: null,
    });
    setFailed(null);
    try {
      await startSet(list, first, rows);
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Could not start that playlist.');
    }
  };
  const createPlaylist = useStore((s) => s.createPlaylist);
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  // The drawer's "New playlist" hands off here rather than carrying a second
  // naming input of its own, so there is one place that makes a playlist and
  // you land where the new one will appear. Router STATE rather than a query
  // param: this is a one-shot intent, and a reload should not re-trigger it.
  const location = useLocation();
  const wantsCreate = (location.state as { create?: boolean } | null)?.create === true;
  const [creating, setCreating] = useState(wantsCreate);
  // Read on every arrival, not only the first mount: the drawer is on this
  // page too, and navigating to the page you are on does not remount it. Then
  // used up, so Back to this entry does not ask again.
  useEffect(() => {
    if (!wantsCreate) return;
    setCreating(true);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );
  }, [wantsCreate, location.pathname, location.search, navigate]);

  const create = () => {
    const name = draft.trim();
    if (!name) return;
    navigate(`${APP_ROUTES.playlists}/${createPlaylist(name)}`);
    setDraft('');
    setCreating(false);
  };

  return (
    <div className="playlists-page" data-testid="playlists-page">
      <header className="playlists-head">
        <h1>Playlists</h1>
        {!creating && (
          <button
            className="btn btn-primary btn-sm"
            data-testid="playlist-new"
            onClick={() => setCreating(true)}
          >
            <Plus size={14} aria-hidden="true" /> New playlist
          </button>
        )}
      </header>

      {creating && (
        <div className="playlist-create-row">
          <input
            className="text-input"
            data-testid="playlist-name-input"
            aria-label="New playlist name"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') create();
              if (e.key === 'Escape') {
                setCreating(false);
                setDraft('');
              }
            }}
          />
          <button className="btn btn-sm btn-primary" data-testid="playlist-create" onClick={create}>
            Create
          </button>
          <button
            className="btn btn-sm btn-ghost"
            aria-label="Cancel new playlist"
            onClick={() => {
              setCreating(false);
              setDraft('');
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {failed && (
        <p className="home-load-error" role="alert">
          {failed}
        </p>
      )}

      {userPlaylists.length === 0 ? (
        <div className="playlists-empty">
          <p className="muted">No playlists yet. Make one here, or add a tune from Home.</p>
          <BrowseTunesButton />
        </div>
      ) : (
        <ul className="playlist-index">
          {userPlaylists.map((p) => (
            <li key={p.id}>
              <Link className="playlist-index-row" to={`${APP_ROUTES.playlists}/${p.id}`}>
                <span className="playlist-index-name">
                  {p.name}
                  {sharedIds.has(p.id) && (
                    <span className="playlist-shared-tag" data-testid="playlist-shared-tag">
                      Shared
                    </span>
                  )}
                </span>
                <span className="muted">{p.entries.length}</span>
              </Link>
              <button
                type="button"
                className="btn btn-sm playlist-index-play"
                data-testid="playlist-play"
                aria-label={`Play ${p.name}`}
                disabled={p.entries.length === 0}
                onClick={() => void play(p)}
              >
                <Play size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {canShare && (
        <SharedWithClass
          shares={sharing}
          error={ownShares.error}
          localIds={new Set(userPlaylists.map((p) => p.id))}
          // The playlist stays, in your account, private.
          onUnshared={(shareId) => ownShares.removeLocal(shareId)}
        />
      )}
    </div>
  );
}

// --- one set list -----------------------------------------------------------

function PlaylistDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const list = useStore((s) => s.userPlaylists.find((p) => p.id === id));
  const userPlaylists = useStore((s) => s.userPlaylists);
  // Your place in the set, if it is this one: coming back to the list shows
  // where you got to (Oct 2026).
  const setCursor = useStore((s) => s.setCursor);
  const hereEntryId = setCursor?.playlistId === id ? setCursor.entryId : null;
  const savedCharts = useStore((s) => s.savedCharts);
  const renamePlaylist = useStore((s) => s.renamePlaylist);
  const deletePlaylist = useStore((s) => s.deletePlaylist);
  const duplicatePlaylist = useStore((s) => s.duplicatePlaylist);
  const movePlaylistEntry = useStore((s) => s.movePlaylistEntry);
  const removeFromPlaylist = useStore((s) => s.removeFromPlaylist);
  const setPlaylistEntryNote = useStore((s) => s.setPlaylistEntryNote);
  // Shared with you view only: the sharer's to change, yours to play.
  const editable = useStore((s) => canEditPlaylist(s.playlistLinks, id));

  const [openbook, setOpenbook] = useState<OpenbookLibraryEntry[]>([]);
  const [realbook, setRealbook] = useState<RealbookLibraryEntry[]>([]);
  const library = useLibraryTunes(!!list?.entries.some((e) => e.ref.kind === 'library'));
  const [renaming, setRenaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const startSet = useStartSet();

  useEffect(() => {
    void openbookLibrary().then(setOpenbook);
    void realbookLibrary().then(setRealbook);
  }, []);

  const rows = useMemo(
    () =>
      list
        ? playlistRows(list, {
            presets: [],
            savedCharts,
            openbook,
            realbook,
            library,
            userPlaylists: [],
            playlist: null,
          })
        : [],
    [list, savedCharts, openbook, realbook, library],
  );

  // A deleted list must not leave the page showing a stale set.
  if (!list) {
    return (
      <div className="playlists-page" data-testid="playlists-page">
        <p className="muted">
          That playlist is gone. <Link to={APP_ROUTES.playlists}>Back to playlists</Link>.
        </p>
      </div>
    );
  }

  const open = async (row: TuneSourceEntry) => {
    setFailed(null);
    try {
      await row.load();
      navigate(APP_ROUTES.workbench);
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Could not load that tune.');
    }
  };

  const move = (entryId: string, to: number) => movePlaylistEntry(list.id, entryId, to);
  // The other playlists, one tap away: from one to the next was back then
  // pick, or the drawer.
  const others = userPlaylists.filter((p) => p.id !== id);

  /** Play the set from this row on. */
  const play = async (row: TuneSourceEntry) => {
    const from = list.entries.find((e) => e.entryId === row.playlistEntryId);
    if (!from) return;
    setFailed(null);
    try {
      await startSet(list, from, rows);
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Could not start that playlist.');
    }
  };

  return (
    <div className="playlists-page" data-testid="playlists-page">
      <header className="playlists-head">
        <Link className="btn btn-sm btn-ghost" to={APP_ROUTES.playlists}>
          <ChevronLeft size={16} aria-hidden="true" /> Playlists
        </Link>
        <div className="playlist-detail-actions">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            data-testid="playlist-play"
            aria-label={`Play ${list.name}`}
            disabled={rows.length === 0}
            onClick={() => rows[0] && void play(rows[0])}
          >
            <Play size={14} aria-hidden="true" /> Play
          </button>
          <button
            className="btn btn-sm btn-ghost"
            aria-label={`Duplicate ${list.name}`}
            onClick={() => {
              const copyId = duplicatePlaylist(list.id);
              if (copyId) navigate(`${APP_ROUTES.playlists}/${copyId}`);
            }}
          >
            <Copy size={14} aria-hidden="true" />
            <span className="playlist-action-label">Duplicate</span>
          </button>
          {confirmDelete ? (
            <button
              className="btn btn-sm btn-danger"
              aria-label={`Confirm delete ${list.name}`}
              onBlur={() => setConfirmDelete(false)}
              onClick={() => {
                deletePlaylist(list.id);
                navigate(APP_ROUTES.playlists);
              }}
            >
              Delete?
            </button>
          ) : (
            <button
              className="btn btn-sm btn-ghost"
              aria-label={`Delete ${list.name}`}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </header>

      {renaming && editable ? (
        <input
          className="text-input playlist-title-input"
          data-testid="playlist-rename-input"
          aria-label={`Rename ${list.name}`}
          defaultValue={list.name}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              renamePlaylist(list.id, (e.target as HTMLInputElement).value);
              setRenaming(false);
            }
            if (e.key === 'Escape') setRenaming(false);
          }}
          onBlur={(e) => {
            renamePlaylist(list.id, e.target.value);
            setRenaming(false);
          }}
        />
      ) : (
        <h1 className="playlist-title">
          {list.name}
          {editable && (
            <button
              className="btn btn-sm btn-ghost"
              aria-label={`Rename ${list.name}`}
              onClick={() => setRenaming(true)}
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          )}
        </h1>
      )}

      <SharePlaylist list={list} />

      {failed && (
        <p className="home-load-error" role="alert">
          {failed}
        </p>
      )}

      {rows.length === 0 ? (
        editable ? (
          <div className="playlists-empty">
            <p className="muted">
              Nothing in this playlist yet. Find a tune on Home and add it here.
            </p>
            <BrowseTunesButton />
          </div>
        ) : (
          <p className="muted">Nothing in this playlist yet.</p>
        )
      ) : (
        <ol className="playlist-set" data-testid="playlist-set">
          {rows.map((row, i) => {
            const entryId = row.playlistEntryId!;
            // A saved call says how the playlist plays the tune: its key and
            // feel replace the tune's own on the row.
            const call = playlistCallSummary(list.entries.find((e) => e.entryId === entryId)?.call);
            const here = entryId === hereEntryId;
            return (
              <li
                key={row.key}
                className={`playlist-set-row${dragging === entryId ? ' is-dragging' : ''}${
                  editable ? '' : ' is-read-only'
                }${here ? ' is-here' : ''}`}
                aria-current={here ? 'true' : undefined}
                draggable={editable}
                onDragStart={() => setDragging(entryId)}
                onDragEnd={() => setDragging(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragging && editable) move(dragging, i);
                  setDragging(null);
                }}
              >
                {editable && (
                  <span className="playlist-set-grip" aria-hidden="true">
                    <GripVertical size={14} />
                  </span>
                )}
                <span className="playlist-set-index muted">{i + 1}</span>

                <button
                  type="button"
                  className="playlist-set-open"
                  onClick={() => void open(row)}
                  // Named explicitly: the button's own text is title + key +
                  // style, which a screen reader would read as a bare list of
                  // facts with no hint that activating it opens the tune.
                  aria-label={`Open ${row.title} in the Workbench`}
                  title={`Open ${row.title} in the Workbench`}
                  // Alt+arrows so the list is reorderable without a mouse. Plain
                  // arrows would fight the browser's own focus movement.
                  onKeyDown={(e) => {
                    if (!e.altKey || !editable) return;
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      move(entryId, i - 1);
                    }
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      move(entryId, i + 1);
                    }
                  }}
                >
                  <span className="playlist-set-title">
                    {row.title}
                    {here && (
                      <span className="playlist-set-here" data-testid="playlist-set-here">
                        You are here
                      </span>
                    )}
                    {row.resolvable === 'missing' && (
                      <span className="set-queue-gone">unavailable</span>
                    )}
                    {row.melody && (
                      <span
                        className="tune-melody"
                        title="Has a written melody"
                        aria-label="has melody"
                      >
                        ♪
                      </span>
                    )}
                  </span>
                  {/* Composer only, and no source badge: `tuneSubtitle` appends
                      the owning playlist's name, which on the playlist's own
                      page just repeats the heading. */}
                  {row.composer && (
                    <span className="playlist-set-subtitle muted">{row.composer}</span>
                  )}
                  {call ? (
                    <span
                      className="playlist-set-meta playlist-set-call"
                      data-testid="playlist-call-summary"
                    >
                      {call}
                    </span>
                  ) : (
                    <span className="playlist-set-meta muted">
                      {[row.musicKey, row.style].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </button>

                {!editable ? (
                  row.note && <span className="playlist-note is-read-only">{row.note}</span>
                ) : noteFor === entryId ? (
                  <input
                    className="text-input playlist-note-input"
                    data-testid="playlist-note-input"
                    aria-label={`Note for ${row.title}`}
                    defaultValue={row.note ?? ''}
                    placeholder="Trade 8s, cue the head out…"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setPlaylistEntryNote(
                          list.id,
                          entryId,
                          (e.target as HTMLInputElement).value,
                        );
                        setNoteFor(null);
                      }
                      if (e.key === 'Escape') setNoteFor(null);
                    }}
                    onBlur={(e) => {
                      setPlaylistEntryNote(list.id, entryId, e.target.value);
                      setNoteFor(null);
                    }}
                  />
                ) : (
                  <button
                    className={`playlist-note${row.note ? '' : ' is-empty'}`}
                    aria-label={
                      row.note ? `Edit note for ${row.title}` : `Add a note to ${row.title}`
                    }
                    onClick={() => setNoteFor(entryId)}
                  >
                    {row.note ?? <Pencil size={13} aria-hidden="true" />}
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  data-testid="playlist-row-play"
                  aria-label={`Play the playlist from ${row.title}`}
                  onClick={() => void play(row)}
                >
                  <Play size={13} aria-hidden="true" />
                </button>

                {editable && (
                  <>
                    <div className="playlist-set-move">
                      <button
                        className="btn btn-sm btn-ghost"
                        aria-label={`Move ${row.title} up`}
                        disabled={i === 0}
                        onClick={() => move(entryId, i - 1)}
                      >
                        <ArrowUp size={15} aria-hidden="true" />
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        aria-label={`Move ${row.title} down`}
                        disabled={i === rows.length - 1}
                        onClick={() => move(entryId, i + 1)}
                      >
                        <ArrowDown size={15} aria-hidden="true" />
                      </button>
                    </div>

                    <button
                      className="btn btn-sm btn-ghost"
                      aria-label={`Remove ${row.title} from ${list.name}`}
                      onClick={() => removeFromPlaylist(list.id, entryId)}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </>
                )}
                <PlaylistScoreUpdate row={row} onUpdated={() => setFailed(null)} />
              </li>
            );
          })}
        </ol>
      )}

      {others.length > 0 && (
        <nav
          className="playlist-siblings"
          aria-label="Other playlists"
          data-testid="playlist-siblings"
        >
          <h2 className="playlist-siblings-heading">Other playlists</h2>
          <ul className="home-playlist-row">
            {others.map((p) => (
              <li key={p.id}>
                <Link className="home-playlist-chip" to={`${APP_ROUTES.playlists}/${p.id}`}>
                  {p.name} <span className="muted">· {p.entries.length}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
